/**
 * Compares the option sets a Quick Entry row can choose from, before and after a confirmed Request
 * Type/Purpose change (AB#35149).
 *
 * The applicable record types are server-computed per Purpose, so changing Purpose can change what
 * Records Requested is allowed to offer. The old code assumed it always did: every purpose change
 * ran `setState({ quickEntryReleaseInfo: null })` and discarded the specialist's selections
 * outright. If a record type is valid for both the old and the new combination, re-picking it is
 * busywork - and until AB#35149 it also happened on changes that were never even confirmed.
 *
 * All-or-nothing per row, by dev decision: identical set -> keep the selection untouched; any
 * difference -> clear the row and let the specialist re-pick, rather than trying to salvage the
 * still-valid subset and leaving them to spot what silently vanished.
 *
 * `undefined` is returned when either side has not loaded, and is NOT a third kind of "different".
 * Treating a not-yet-arrived list as an empty set would wipe a valid selection on a transient
 * state; treating it as equal would keep a selection the new purpose may not allow. Neither is
 * safe, so the caller is told it cannot decide yet and must wait (the AB#35083 lesson - an empty
 * list means "not loaded", never "no options").
 *
 * Never logs a value - option names are facility configuration, and the surrounding rows carry PHI.
 */
export interface IdentifiedOption {
    readonly id?: number | string;
    /** Callers pass full Release / institution records; only `id` is read. */
    readonly [key: string]: any;
}

/**
 * Real option ids are positive; 0 / negative / NaN / absent are sentinels, not members.
 * Returns a sorted, de-duplicated id list rather than a Set - the project targets es5 without
 * `downlevelIteration`, so a Set cannot be iterated here.
 */
function idList(options: readonly IdentifiedOption[]): number[] {
    const ids: number[] = [];
    options.forEach(option => {
        const n = Number(option?.id);
        if (Number.isFinite(n) && n > 0 && ids.indexOf(n) === -1) {
            ids.push(n);
        }
    });
    return ids.sort((a, b) => a - b);
}

export function optionSetsEqual(
    before: readonly IdentifiedOption[] | undefined,
    after: readonly IdentifiedOption[] | undefined,
): boolean | undefined {
    if (!before || !after) {
        return undefined; // not loaded - the caller must not act on this yet
    }

    const a = idList(before);
    const b = idList(after);
    return a.length === b.length && a.every((id, i) => id === b[i]);
}

export default optionSetsEqual;
